import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { TransactionReportsStatisticsByChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction-report/transaction-reports-statistics-by-charge-point.model';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import * as css from '../../statistics.module.scss';

export enum MoreLessCardVariantEnum {
  Transactions = 'transactions',
  Energy = 'energy',
  Revenue = 'revenue',
}

interface MoreLessVariation {
  name: string;
  color: 'primary' | 'secondary' | 'decorative';
  titleVariant: AccentVariantEnum;
}

const moreLessVariations: Record<MoreLessCardVariantEnum, MoreLessVariation> = {
  [MoreLessCardVariantEnum.Transactions]: {
    name: 'transactions',
    color: 'primary',
    titleVariant: AccentVariantEnum.Primary,
  },
  [MoreLessCardVariantEnum.Energy]: {
    name: 'energy',
    color: 'secondary',
    titleVariant: AccentVariantEnum.Secondary,
  },
  [MoreLessCardVariantEnum.Revenue]: {
    name: 'revenue',
    color: 'decorative',
    titleVariant: AccentVariantEnum.Accent1,
  },
};

interface MoreLessCardProps {
  data?: TransactionReportsStatisticsByChargePointModel;
  variant?: MoreLessCardVariantEnum;
}

export const MoreLessCard = ({
  data,
  variant = MoreLessCardVariantEnum.Transactions,
}: MoreLessCardProps): React.JSX.Element => {
  const { t } = useTranslation();

  const { name, color, titleVariant } = moreLessVariations[variant];

  const moreChargePointName =
    data?.value.reduce((max, current) =>
      (current.total ?? 0) > (max.total ?? 0) ? current : max,
    ).chargePointName ?? '-';

  const lessChargePointName =
    data?.value.reduce((min, current) =>
      (current.total ?? 0) < (min.total ?? 0) ? current : min,
    ).chargePointName ?? '-';

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        variant={titleVariant}
        icon="electric-terminal-3"
        text={t('$.statistics.cards.more-less.title')}
      />
      <Stack
        alignItems="center"
        className={classNames([css.highlighted, css[`highlighted-${color}`]])}
        direction="row"
        gap={GapEnum.M}
        justifyContent="space-between">
        <Stack
          alignItems="center"
          gap={GapEnum.XS}
          flex={1}
          justifyContent="flex-start">
          <Text fontSize={TextFontSize.Large}>
            {t(`$.statistics.cards.more-less.more-${name}`)}
          </Text>
        </Stack>
        <Text bold fontSize={TextFontSize.Large}>
          {moreChargePointName}
        </Text>
      </Stack>
      <Stack
        alignItems="center"
        className={classNames([css.highlighted, css[`highlighted-${color}`]])}
        direction="row"
        gap={GapEnum.M}
        justifyContent="space-between">
        <Stack
          alignItems="center"
          gap={GapEnum.XS}
          flex={1}
          justifyContent="flex-start">
          <Text fontSize={TextFontSize.Large}>
            {t(`$.statistics.cards.more-less.less-${name}`)}
          </Text>
        </Stack>
        <Text bold fontSize={TextFontSize.Large}>
          {lessChargePointName}
        </Text>
      </Stack>
    </Card>
  );
};
